import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, uploadFile, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import StatusBadge from "@/components/StatusBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import ExportPreview from "@/components/ExportPreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Eye, Download, Upload, Camera, FileSpreadsheet, ArrowRightLeft, FileText, IdCard, GitMerge } from "lucide-react";
import { CameraCapture } from "@/components/CameraCapture";
import MergeEmployeeDialog from "@/components/MergeEmployeeDialog";
import { toast } from "sonner";

const STATUS = ["Harian", "Borongan", "Staff", "Supervisor", "Freelance"];
const empty = {
  nik: "", nama: "", nama_panggilan: "", jabatan: "", skill: "", no_hp: "", no_wa: "", alamat: "",
  tanggal_mulai_kerja: "", project_id: "", lokasi: "", status_pegawai: "Harian", aktif: true,
  nama_bank: "", no_rekening: "", nama_pemilik_rekening: "", foto_url: "", foto_ktp_url: "", sertifikat_url: "", catatan: "",
  pimpinan_id: "", is_pimpinan: false, sertifikat_keahlian: false,
};

export default function Employees() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canWrite = ["super_admin", "hr", "direktur", "site_admin"].includes(user.role);
  const canManage = ["super_admin", "hr", "direktur"].includes(user.role);
  const canMerge = ["super_admin", "direktur"].includes(user.role);
  const [merge, setMerge] = useState(null);
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [del, setDel] = useState(null);
  const [confirmExport, setConfirmExport] = useState(false);
  const [selected, setSelected] = useState([]);
  const [visibleIds, setVisibleIds] = useState([]);
  const [statusF, setStatusF] = useState("all");
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfDetailOpen, setPdfDetailOpen] = useState(false);
  const [idCardOpen, setIdCardOpen] = useState(false);
  const toggleRow = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = (ids, checked) => setSelected((s) => checked ? Array.from(new Set([...s, ...ids])) : s.filter((x) => !ids.includes(x)));
  const pdfIds = selected.length ? selected : visibleIds;
  const pdfQuery = pdfIds.length ? `?ids=${pdfIds.join(",")}` : "";
  const [showRek, setShowRek] = useState(true);
  const detailQuery = `${pdfQuery ? `${pdfQuery}&` : "?"}rekening=${showRek ? 1 : 0}`;
  const [importPreview, setImportPreview] = useState(null);
  const [transfer, setTransfer] = useState(null);

  const saveTransfer = async () => {
    try {
      await api.post(`/employees/${transfer.id}/transfer`, {
        project_id: transfer.project_id || "", pimpinan_id: transfer.pimpinan_id || "",
        lokasi: transfer.lokasi || "", tanggal: transfer.tanggal || "",
      });
      toast.success("Pegawai berhasil ditransfer");
      setTransfer(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const [cam, setCam] = useState(null); // {field, id} — id null => form baru/edit

  const load = () => api.get("/employees").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);
  const projName = (id) => projects.find((p) => p.id === id)?.nama || "-";
  const pimpinanName = (id) => rows.find((r) => r.id === id)?.nama || "-";
  const pimpinanList = rows.filter((r) => r.is_pimpinan);

  const openNew = () => { setForm(empty); setEditing(null); setDupes([]); setOpen(true); };
  const openEdit = (r) => { setForm({ ...empty, ...r }); setEditing(r.id); setDupes([]); setOpen(true); };

  const doUploadToForm = async (file, field) => {
    try {
      const r = await uploadFile(file, field === "foto_ktp_url" ? "ktp" : "foto");
      setForm((f) => ({ ...f, [field]: r.path }));
      toast.success("Foto terunggah");
    } catch (err) { toast.error(apiErr(err)); }
  };
  const uploadFoto = (e, field) => { const file = e.target.files?.[0]; if (file) doUploadToForm(file, field); e.target.value = ""; };

  const uploadSertifikat = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const r = await uploadFile(file, "sertifikat");
      setForm((f) => ({ ...f, sertifikat_url: r.path, sertifikat_keahlian: true }));
      toast.success("File sertifikat terunggah");
    } catch (err) { toast.error(apiErr(err)); }
  };

  const doUploadQuick = async (file, id, field) => {
    try {
      const r = await uploadFile(file, field === "foto_ktp_url" ? "ktp" : "foto");
      await api.put(`/employees/${id}/photo`, { field, path: r.path });
      toast.success("Foto diperbarui"); load();
    } catch (err) { toast.error(apiErr(err)); }
  };
  const quickPhoto = (e, id, field) => { const f = e.target.files?.[0]; if (f) doUploadQuick(f, id, field); e.target.value = ""; };

  const onCapture = (file) => {
    if (cam?.id) doUploadQuick(file, cam.id, cam.field);
    else doUploadToForm(file, cam.field);
  };

  const removePhoto = async (id, field) => {
    try { await api.put(`/employees/${id}/photo`, { field, path: "" }); toast.success("Foto dihapus"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const [dupes, setDupes] = useState([]);
  const [dupConfirm, setDupConfirm] = useState(null);
  const checkDupes = async () => {
    if (!form.nama && !form.nik) return setDupes([]);
    try {
      const q = new URLSearchParams({ nama: form.nama || "", nik: form.nik || "", exclude_id: editing || "" });
      const r = await api.get(`/employees-check-duplicate?${q}`);
      setDupes(r.data || []);
    } catch { setDupes([]); }
  };
  const save = async (force = false) => {
    if (!form.nama) return toast.error("Nama wajib diisi");
    const qs = force ? "?force=true" : "";
    try {
      if (editing) await api.put(`/employees/${editing}${qs}`, form);
      else await api.post(`/employees${qs}`, form);
      toast.success("Data pegawai tersimpan");
      setOpen(false); setDupConfirm(null); setDupes([]);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.duplicates) return setDupConfirm(d.duplicates);
      toast.error(apiErr(e));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/employees/${del.id}`);
      toast.success("Pegawai dihapus");
      setDel(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const exportXlsx = async () => {
    try {
      const r = await api.get("/employees-export/xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "data_pegawai.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/employees-template/xlsx", { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = "template_import_pegawai.xlsx"; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const importXlsx = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("commit", "false");
    try {
      const r = await api.post("/employees-import", fd);
      if (!r.data.count) toast.error("Tidak ada data pegawai terbaca di file ini");
      else setImportPreview({ file, rows: r.data.preview });
    } catch (err) { toast.error(apiErr(err)); }
    e.target.value = "";
  };

  const confirmImport = async () => {
    const fd = new FormData(); fd.append("file", importPreview.file); fd.append("commit", "true");
    try {
      const r = await api.post("/employees-import", fd);
      const sk = r.data.skipped || 0;
      toast.success(`${r.data.created} pegawai diimport${sk ? `, ${sk} dilewati (duplikat)` : ""}`);
      setImportPreview(null); load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const columns = [
    { key: "kode_pegawai", label: "Kode", sortable: true },
    {
      key: "foto", label: "Foto", render: (r) => (
        <div className="flex items-center gap-1.5">
          {r.foto_url
            ? <img src={fileUrl(r.foto_url)} alt="" className="h-9 w-9 rounded-full object-cover border" />
            : <span className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-400">{(r.nama || "?")[0]}</span>}
          {canWrite && (
            <div className="flex flex-col leading-none gap-0.5">
              <label className="text-[11px] text-blue-700 underline cursor-pointer">
                <input type="file" accept="image/*" className="hidden" data-testid={`emp-foto-${r.id}`} onChange={(e) => quickPhoto(e, r.id, "foto_url")} />
                {r.foto_url ? "Ganti" : "Unggah"}
              </label>
              <button className="flex items-center gap-0.5 text-[11px] text-emerald-700" data-testid={`emp-foto-cam-${r.id}`} onClick={() => setCam({ field: "foto_url", id: r.id })}><Camera className="h-3 w-3" /> Kamera</button>
              {r.foto_url && <button className="text-[11px] text-rose-600" data-testid={`emp-foto-del-${r.id}`} onClick={() => removePhoto(r.id, "foto_url")}>Hapus</button>}
            </div>
          )}
        </div>
      ),
    },
    { key: "nama", label: "Nama", sortable: true, render: (r) => <span className="font-semibold text-slate-800">{r.nama}</span> },
    { key: "jabatan", label: "Jabatan", sortable: true },
    { key: "status_pegawai", label: "Status", sortable: true },
    { key: "project_id", label: "Proyek", render: (r) => projName(r.project_id) },
    { key: "pimpinan", label: "Pimpinan", render: (r) => r.is_pimpinan ? <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700">PIMPINAN</span> : (r.pimpinan_id ? pimpinanName(r.pimpinan_id) : <span className="text-slate-300">—</span>) },
    { key: "lokasi", label: "Lokasi" },
    { key: "aktif", label: "Aktif", render: (r) => r.merged_into ? <span className="rounded bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600" data-testid={`emp-merged-${r.id}`}>Digabung</span> : <StatusBadge status={r.aktif ? "Aktif" : "Nonaktif"} /> },
    {
      key: "act", label: "Aksi", render: (r) => (
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" data-testid={`emp-detail-${r.id}`} onClick={() => nav(`/employees/${r.id}`)}><Eye className="h-4 w-4" /></Button>
          {canWrite && <Button size="icon" variant="ghost" data-testid={`emp-edit-${r.id}`} onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>}
          {canManage && <Button size="icon" variant="ghost" className="text-amber-600" data-testid={`emp-transfer-${r.id}`} onClick={() => setTransfer({ id: r.id, nama: r.nama, project_id: r.project_id || "", pimpinan_id: r.pimpinan_id || "", lokasi: r.lokasi || "", tanggal: "" })}><ArrowRightLeft className="h-4 w-4" /></Button>}
          {canMerge && !r.merged_into && <Button size="icon" variant="ghost" className="text-orange-600" title="Gabung data kembar" data-testid={`emp-merge-${r.id}`} onClick={() => setMerge(r)}><GitMerge className="h-4 w-4" /></Button>}
          {canManage && <Button size="icon" variant="ghost" className="text-red-600" data-testid={`emp-delete-${r.id}`} onClick={() => setDel(r)}><Trash2 className="h-4 w-4" /></Button>}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Data Karyawan" subtitle="Master data pegawai harian & project">
        {canWrite && <>
          <Button variant="outline" onClick={() => setPdfOpen(true)} data-testid="emp-pdf-btn"><FileText className="h-4 w-4 mr-1" /> Preview PDF{selected.length ? ` (${selected.length})` : ""}</Button>
          <Button variant="outline" onClick={() => setPdfDetailOpen(true)} data-testid="emp-pdf-detail-btn"><FileText className="h-4 w-4 mr-1" /> Preview Detail{selected.length ? ` (${selected.length})` : ""}</Button>
          <Button variant="outline" className="border-orange-300 text-orange-700" onClick={() => setIdCardOpen(true)} data-testid="emp-idcard-btn"><IdCard className="h-4 w-4 mr-1" /> Kartu ID{selected.length ? ` (${selected.length})` : ""}</Button>
          <Button variant="outline" onClick={() => setConfirmExport(true)} data-testid="emp-export-btn"><Download className="h-4 w-4 mr-1" /> Export</Button>
          <Button variant="outline" onClick={downloadTemplate} data-testid="emp-template-btn"><FileSpreadsheet className="h-4 w-4 mr-1" /> Template</Button>
          <label>
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={importXlsx} data-testid="emp-import-input" />
            <Button variant="outline" asChild><span><Upload className="h-4 w-4 mr-1" /> Import</span></Button>
          </label>
          <Button className="bg-blue-800 hover:bg-blue-900" onClick={openNew} data-testid="emp-add-btn"><Plus className="h-4 w-4 mr-1" /> Tambah</Button>
        </>}
      </PageHeader>

      <DataTable columns={columns} data={rows.filter((r) => statusF === "all" || (statusF === "aktif" ? r.aktif : !r.aktif))} searchKeys={["nama", "kode_pegawai", "jabatan", "nik", "lokasi"]} testid="emp-table"
        toolbar={<StatusFilter rows={rows} value={statusF} onChange={setStatusF} />}
        selectable selectedIds={selected} onToggleRow={toggleRow} onToggleAll={toggleAll} onVisibleChange={setVisibleIds} />

      <ExportPreview open={pdfOpen} onClose={() => setPdfOpen(false)}
        title={`Pratinjau Data Karyawan (${pdfIds.length || rows.length} pegawai${selected.length ? " terpilih" : ""})`}
        pdfUrl={`/reports/employees/pdf${pdfQuery}`}
        items={[{ label: "Unduh PDF", url: `/reports/employees/pdf${pdfQuery}`, filename: "data_karyawan.pdf" }]} />

      <ExportPreview open={pdfDetailOpen} onClose={() => setPdfDetailOpen(false)}
        title={`Detail Data Karyawan (${pdfIds.length || rows.length} pegawai${selected.length ? " terpilih" : ""})`}
        pdfUrl={`/reports/employees/pdf-detail${detailQuery}`}
        items={[{ label: "Unduh PDF", url: `/reports/employees/pdf-detail${detailQuery}`, filename: "data_karyawan_detail.pdf" }]}
        toolbar={<label className="flex items-center gap-2 text-sm text-slate-700" data-testid="emp-detail-rek-toggle">
          <input type="checkbox" className="h-4 w-4 accent-blue-800" checked={showRek} onChange={(e) => setShowRek(e.target.checked)} /> Tampilkan data rekening
        </label>} />
      <ExportPreview open={idCardOpen} onClose={() => setIdCardOpen(false)}
        title={`Kartu ID Karyawan (${pdfIds.length || rows.length} pegawai${selected.length ? " terpilih" : ""})`}
        pdfUrl={`/reports/employees/id-cards${pdfQuery}`}
        items={[{ label: "Unduh PDF (siap cetak)", url: `/reports/employees/id-cards${pdfQuery}`, filename: "kartu_id_karyawan.pdf" }]} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Tambah"} Pegawai</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="NIK / KTP"><Input data-testid="emp-f-nik" value={form.nik} onBlur={checkDupes} onChange={(e) => setForm({ ...form, nik: e.target.value })} /></F>
            <F label="Nama Lengkap *"><Input data-testid="emp-f-nama" value={form.nama} onBlur={checkDupes} onChange={(e) => setForm({ ...form, nama: e.target.value })} /></F>
            {dupes.length > 0 && <div className="sm:col-span-2"><DupList items={dupes} testid="emp-dup-hint" /></div>}
            <F label="Nama Panggilan"><Input value={form.nama_panggilan} onChange={(e) => setForm({ ...form, nama_panggilan: e.target.value })} /></F>
            <F label="Jabatan"><Input value={form.jabatan} onChange={(e) => setForm({ ...form, jabatan: e.target.value })} /></F>
            <F label="Keahlian (Skill)"><Input value={form.skill} onChange={(e) => setForm({ ...form, skill: e.target.value })} /></F>
            <F label="Sertifikat Keahlian">
              <label className="flex h-10 items-center gap-2 rounded-md border border-slate-200 px-3 text-sm" data-testid="emp-f-sertifikat">
                <input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={!!form.sertifikat_keahlian}
                  onChange={(e) => setForm({ ...form, sertifikat_keahlian: e.target.checked })} /> Punya sertifikat keahlian
              </label>
            </F>
            <F label="No. HP"><Input value={form.no_hp} onChange={(e) => setForm({ ...form, no_hp: e.target.value })} /></F>
            <F label="No. WhatsApp (kirim slip)"><Input data-testid="emp-f-nowa" placeholder="08xxx / 62xxx" value={form.no_wa} onChange={(e) => setForm({ ...form, no_wa: e.target.value })} /></F>
            <F label="Status Pegawai">
              <Select value={form.status_pegawai} onValueChange={(v) => setForm({ ...form, status_pegawai: v })}>
                <SelectTrigger data-testid="emp-f-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Proyek Aktif">
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="emp-f-project"><SelectValue placeholder="Pilih proyek" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">- Tidak ada -</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Lokasi Kerja"><Input value={form.lokasi} onChange={(e) => setForm({ ...form, lokasi: e.target.value })} /></F>
            <F label="Tanggal Mulai Kerja"><Input type="date" value={form.tanggal_mulai_kerja} onChange={(e) => setForm({ ...form, tanggal_mulai_kerja: e.target.value })} /></F>
            <F label="Nama Bank"><Input value={form.nama_bank} onChange={(e) => setForm({ ...form, nama_bank: e.target.value })} /></F>
            <F label="No. Rekening"><Input value={form.no_rekening} onChange={(e) => setForm({ ...form, no_rekening: e.target.value })} /></F>
            <F label="Nama Pemilik Rekening"><Input value={form.nama_pemilik_rekening} onChange={(e) => setForm({ ...form, nama_pemilik_rekening: e.target.value })} /></F>
            <F label="Foto Pegawai">
              <div className="flex items-center gap-2">
                {form.foto_url && <img src={fileUrl(form.foto_url)} alt="foto" className="h-12 w-12 rounded-lg object-cover border" />}
                <label className="cursor-pointer text-sm text-blue-700 underline">
                  <input type="file" accept="image/*" className="hidden" data-testid="emp-f-foto" onChange={(e) => uploadFoto(e, "foto_url")} />
                  {form.foto_url ? "Ganti" : "Unggah"}
                </label>
                <button type="button" className="flex items-center gap-1 text-sm text-emerald-700 underline" data-testid="emp-f-foto-cam" onClick={() => setCam({ field: "foto_url", id: null })}><Camera className="h-4 w-4" /> Kamera</button>
              </div>
            </F>
            <F label="Foto KTP">
              <div className="flex items-center gap-2">
                {form.foto_ktp_url && <img src={fileUrl(form.foto_ktp_url)} alt="ktp" className="h-12 w-20 rounded-lg object-cover border" />}
                <label className="cursor-pointer text-sm text-blue-700 underline">
                  <input type="file" accept="image/*" className="hidden" data-testid="emp-f-ktp" onChange={(e) => uploadFoto(e, "foto_ktp_url")} />
                  {form.foto_ktp_url ? "Ganti" : "Unggah"}
                </label>
                <button type="button" className="flex items-center gap-1 text-sm text-emerald-700 underline" data-testid="emp-f-ktp-cam" onClick={() => setCam({ field: "foto_ktp_url", id: null })}><Camera className="h-4 w-4" /> Kamera</button>
              </div>
            </F>
            <F label="File Sertifikat (foto / PDF)">
              <div className="flex items-center gap-2">
                {form.sertifikat_url && (/\.pdf$/i.test(form.sertifikat_url)
                  ? <a href={fileUrl(form.sertifikat_url)} target="_blank" rel="noreferrer" className="flex h-12 w-20 items-center justify-center rounded-lg border bg-rose-50 text-xs font-bold text-rose-700" data-testid="emp-f-sert-view">PDF</a>
                  : <img src={fileUrl(form.sertifikat_url)} alt="sertifikat" className="h-12 w-20 rounded-lg object-cover border" />)}
                <label className="cursor-pointer text-sm text-blue-700 underline">
                  <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="emp-f-sert" onChange={uploadSertifikat} />
                  {form.sertifikat_url ? "Ganti" : "Unggah"}
                </label>
                {form.sertifikat_url && <button type="button" className="text-sm text-rose-600 underline" data-testid="emp-f-sert-del" onClick={() => setForm({ ...form, sertifikat_url: "" })}>Hapus</button>}
              </div>
            </F>
            <F label="Status Aktif">
              <Select value={form.aktif ? "1" : "0"} onValueChange={(v) => setForm({ ...form, aktif: v === "1" })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="1">Aktif</SelectItem><SelectItem value="0">Nonaktif</SelectItem></SelectContent>
              </Select>
            </F>
            {canManage && <F label="Pimpinan (penanggung jawab absensi)">
              <Select value={form.pimpinan_id || "none"} onValueChange={(v) => setForm({ ...form, pimpinan_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="emp-f-pimpinan"><SelectValue placeholder="- Tidak ada -" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">- Tidak ada -</SelectItem>
                  {pimpinanList.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>}
            {canManage && <F label="Jadikan Pimpinan?">
              <Select value={form.is_pimpinan ? "1" : "0"} onValueChange={(v) => setForm({ ...form, is_pimpinan: v === "1" })}>
                <SelectTrigger data-testid="emp-f-is-pimpinan"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="0">Bukan</SelectItem><SelectItem value="1">Ya, Pimpinan</SelectItem></SelectContent>
              </Select>
            </F>}
            <div className="sm:col-span-2"><F label="Alamat"><Textarea value={form.alamat} onChange={(e) => setForm({ ...form, alamat: e.target.value })} /></F></div>
            <div className="sm:col-span-2"><F label="Catatan"><Textarea value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} /></F></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => save()} data-testid="emp-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {merge && <MergeEmployeeDialog keep={merge} rows={rows} onClose={() => setMerge(null)} onDone={() => { setMerge(null); load(); }} />}

      <Dialog open={!!dupConfirm} onOpenChange={() => setDupConfirm(null)}>
        <DialogContent className="max-w-lg" data-testid="emp-dup-dialog">
          <DialogHeader><DialogTitle>Karyawan ini mungkin sudah terdaftar</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Data berikut punya nama atau NIK yang sama dengan <b>{form.nama}</b>. Cek dulu agar absensi & Master Gaji tidak terpecah ke dua data.</p>
          <DupList items={dupConfirm || []} testid="emp-dup-list" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupConfirm(null)} data-testid="emp-dup-cancel">Batal, cek dulu</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => save(true)} data-testid="emp-dup-force">Tetap Simpan (orang berbeda)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} destructive testid="emp-delete-dialog"
        title="Hapus Pegawai?" description={`Yakin menghapus ${del?.nama}? Tindakan ini tidak dapat dibatalkan.`}
        confirmText="Hapus" onConfirm={doDelete} />

      <CameraCapture open={!!cam} onClose={() => setCam(null)} onCapture={onCapture}
        title={cam?.field === "foto_ktp_url" ? "Ambil Foto KTP" : "Ambil Foto Pegawai"} />

      <Dialog open={!!transfer} onOpenChange={(o) => { if (!o) setTransfer(null); }}>
        <DialogContent className="max-w-md" data-testid="emp-transfer-dialog">
          <DialogHeader><DialogTitle>Transfer Pegawai — {transfer?.nama}</DialogTitle></DialogHeader>
          {transfer && <div className="space-y-3">
            <F label="Proyek Baru">
              <Select value={transfer.project_id || "none"} onValueChange={(v) => setTransfer({ ...transfer, project_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="emp-transfer-project"><SelectValue placeholder="- Tidak ada -" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">- Tidak ada -</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Pimpinan/Mandor Penerima">
              <Select value={transfer.pimpinan_id || "none"} onValueChange={(v) => setTransfer({ ...transfer, pimpinan_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="emp-transfer-pimpinan"><SelectValue placeholder="- Tidak ada -" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">- Tidak ada -</SelectItem>
                  {pimpinanList.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Lokasi (opsional)"><Input value={transfer.lokasi} data-testid="emp-transfer-lokasi" onChange={(e) => setTransfer({ ...transfer, lokasi: e.target.value })} /></F>
            <F label="Tanggal Transfer (opsional)"><Input type="date" value={transfer.tanggal} data-testid="emp-transfer-tanggal" onChange={(e) => setTransfer({ ...transfer, tanggal: e.target.value })} /></F>
            <p className="text-xs text-slate-500">Setelah transfer, tanggung jawab absensi berpindah ke Pimpinan/Mandor penerima.</p>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransfer(null)}>Batal</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={saveTransfer} data-testid="emp-transfer-save">Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <ConfirmDialog open={confirmExport} onOpenChange={() => setConfirmExport(false)} testid="emp-export-dialog"
        title="Unduh Data Pegawai?" description={`Tabel di layar adalah pratinjau. Unduh ${rows.length} pegawai sebagai file Excel?`}
        confirmText="Unduh Excel" onConfirm={() => { exportXlsx(); setConfirmExport(false); }} />

      <Dialog open={!!importPreview} onOpenChange={(o) => { if (!o) setImportPreview(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="emp-import-preview">
          <DialogHeader><DialogTitle>Pratinjau Import Pegawai ({importPreview?.rows?.length || 0})</DialogTitle></DialogHeader>
          {importPreview?.rows?.some((r) => r.duplikat) && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="emp-import-dup-note">
              {importPreview.rows.filter((r) => r.duplikat).length} baris terdeteksi duplikat (nama/NIK sudah ada) dan akan DILEWATI saat import.
            </div>
          )}
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-slate-100 text-xs font-bold uppercase text-slate-500">
                <th className="px-3 py-2 text-left">Nama</th><th className="px-3 py-2 text-left">NIK</th>
                <th className="px-3 py-2 text-left">Jabatan</th><th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">No HP</th><th className="px-3 py-2 text-left">Bank</th>
                <th className="px-3 py-2 text-left">Ket.</th>
              </tr></thead>
              <tbody>
                {(importPreview?.rows || []).map((r, i) => (
                  <tr key={i} className={`border-b border-slate-100 ${r.duplikat ? "bg-amber-50/60 text-slate-400" : ""}`} data-testid={`emp-import-row-${i}`}>
                    <td className="px-3 py-1.5 font-medium">{r.nama}</td><td className="px-3 py-1.5">{r.nik || "-"}</td>
                    <td className="px-3 py-1.5">{r.jabatan || "-"}</td><td className="px-3 py-1.5">{r.status_pegawai}</td>
                    <td className="px-3 py-1.5">{r.no_hp || "-"}</td><td className="px-3 py-1.5">{r.nama_bank || "-"}</td>
                    <td className="px-3 py-1.5">{r.duplikat ? <span className="rounded bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Duplikat</span> : <span className="rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Baru</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportPreview(null)}>Batal</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={confirmImport} data-testid="emp-import-confirm">Konfirmasi Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const DupList = ({ items, testid }) => (
  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid={testid}>
    <div className="mb-1 font-semibold">Perhatian: nama / NIK sudah terdaftar</div>
    <ul className="space-y-1">{items.map((d) => (
      <li key={d.id} className="flex flex-wrap items-center gap-2">
        <a href={`/employees/${d.id}`} target="_blank" rel="noreferrer" className="font-semibold underline">{d.nama}</a>
        <span className="text-xs text-amber-700">{d.kode_pegawai} · {d.proyek}{d.aktif === false ? " · nonaktif" : ""}</span>
        {d.alasan.map((a) => <span key={a} className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${a === "NIK sama" ? "bg-rose-600 text-white" : "bg-amber-200"}`}>{a}</span>)}
      </li>
    ))}</ul>
  </div>
);

const StatusFilter = ({ rows, value, onChange }) => {
  const n = { aktif: rows.filter((r) => r.aktif).length, nonaktif: rows.filter((r) => !r.aktif).length, all: rows.length };
  const opts = [["aktif", "Aktif", "bg-emerald-600"], ["nonaktif", "Nonaktif", "bg-rose-600"], ["all", "Semua", "bg-slate-700"]];
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="emp-status-filter">
      {opts.map(([k, l, c]) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`emp-status-${k}`}
          className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${value === k ? `${c} border-transparent text-white` : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}>
          {l} <span className="ml-1 opacity-80" data-testid={`emp-status-count-${k}`}>{n[k]}</span>
        </button>
      ))}
    </div>
  );
};


export function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
